import React, { FC, MouseEventHandler } from 'react';
import Ripples from 'react-ripples';
import SubAccountStatusEnum from '../../../models/subaccount-status-enum';
import { SubAccountType } from '../../../models/subaccount-type';
import IcomoonIcon from '../../atoms/icomoon-icon/icomoon-icon';
import TableData from '../../atoms/table-data/table-data';
import TableRow from '../../atoms/table-row/table-row';
import { UserManagementType } from '../../../models/user-management-type';
import getTailwindConfig from '../../../helpers/get-tailwind-config';

interface Props extends Partial<UserManagementType>, SubAccountType {
  readonly onDeactivate?: MouseEventHandler<HTMLButtonElement>;
  readonly onResend?: MouseEventHandler<HTMLButtonElement>;
}

const UsersTableRow: FC<Props> = ({
  deactivate,
  email,
  fullName,
  onDeactivate,
  onResend,
  resendInvite,
  status,
  statusActive,
  statusDeactivated,
  statusInviteExpired,
  statusInvited,
  subUsersEmail,
  subUsersName,
  subUsersStatus,
}: Props) => {
  const fullConfig = getTailwindConfig();
  const frenchBlue = fullConfig.theme.colors['french-blue'];

  let backgroundColorClass = 'bg-light-blue';
  let statusColor = 'bg-grey-500';
  let statusName = statusInvited;

  if (status === SubAccountStatusEnum.Active) {
    backgroundColorClass = 'bg-white';
    statusColor = 'bg-turquoise';
    statusName = statusActive;
  } else if (status === SubAccountStatusEnum.Deactivated) {
    backgroundColorClass = 'bg-grey-50';
    statusColor = 'bg-invalid';
    statusName = statusDeactivated;
  } else if (status === SubAccountStatusEnum.InviteExpired) {
    statusColor = 'bg-grey-600';
    statusName = statusInviteExpired;
  }

  return (
    <>
      <TableRow
        className={`${backgroundColorClass} ${
          status === SubAccountStatusEnum.Active ? 'border border-grey-100' : ''
        } grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3`}
      >
        <TableData
          className={`${
            status === SubAccountStatusEnum.Active
              ? 'lg:border-b lg:border-l lg:border-t border-grey-100'
              : ''
          } rounded-l-lg`}
          label={subUsersName}
        >
          {fullName && <span>{fullName}</span>}
        </TableData>

        <TableData
          className={`${
            status === SubAccountStatusEnum.Active
              ? 'lg:border-b lg:border-t border-grey-100'
              : ''
          }`}
          label={subUsersEmail}
        >
          <span className="truncate">{email}</span>
        </TableData>

        <TableData
          label={subUsersStatus}
          className={`${
            status === SubAccountStatusEnum.Active
              ? 'lg:border-b lg:border-t border-grey-100'
              : ''
          }`}
        >
          <div className="flex items-center py-2">
            <div
              className={`${statusColor} h-2 mr-2 rounded-full w-2 flex-shrink-0`}
            />

            <span className="whitespace-nowrap">{statusName}</span>
          </div>
        </TableData>

        <TableData
          className={`${
            status === SubAccountStatusEnum.Active
              ? 'lg:border-b lg:border-r lg:border-t border-grey-100'
              : ''
          } rounded-r-lg`}
          label=""
        >
          <>
            {status === SubAccountStatusEnum.InviteExpired && (
              <div className="m-auto md:ml-auto">
                <Ripples
                  className="rounded-full"
                  color={`rgba(${frenchBlue.rgb}, .2)`}
                  during={1200}
                >
                  <button
                    className="flex items-center focus:outline-none px-4 py-2 rounded-full text-grey-600 text-left"
                    onClick={onResend}
                    type="button"
                  >
                    <IcomoonIcon color="french-blue" name="email" size="lg" />
                    <span className="ml-4 text-french-blue">
                      {resendInvite}
                    </span>
                  </button>
                </Ripples>
              </div>
            )}

            {status === SubAccountStatusEnum.Active && (
              <div className="m-auto md:ml-auto">
                <Ripples
                  className="rounded-full"
                  color={`rgba(${frenchBlue.rgb}, .2)`}
                  during={1200}
                >
                  <button
                    className="flex items-center focus:outline-none px-4 py-2 rounded-full text-grey-600 text-left"
                    onClick={onDeactivate}
                    type="button"
                  >
                    <IcomoonIcon
                      color="french-blue"
                      name="close-circle"
                      size="lg"
                    />
                    <span className="ml-4 text-french-blue">{deactivate}</span>
                  </button>
                </Ripples>
              </div>
            )}
          </>
        </TableData>
      </TableRow>

      <tr className="h-3" />
    </>
  );
};

UsersTableRow.defaultProps = {
  onDeactivate: null,
  onResend: null,
};

export default UsersTableRow;
